import formatISO9075 from 'date-fns/formatISO9075';
import { round } from 'lodash';

type FormattedNumber = {
  value: number;
  unit: string;
  displayValue: string;
};

/**
 * Takes a number and determines if it's 1000 or over and applies shorthand formatting if so.
 * Example: Passing in 1010 will return 1k. Passing in 1230 will return 1.2k.
 * @param number The initial number to format
 * @returns A formatted number
 *
 * For i18n purpose - use number(count, 'compact-short')
 * https://unicode-org.github.io/icu/userguide/format_parse/numbers/skeletons.html#examples
 */
export function formatCount(number = 0) {
  if (number > 999) {
    const countOfThousands = number / 1000;
    if (countOfThousands % 1 !== 0) return `${countOfThousands.toFixed(1)}k`;
    return `${countOfThousands}k`;
  }

  return `${number}`;
}

/**
 * Converts the given time value in seconds to a value that can be used
 * across browsers.
 *
 * @param {number | null} time - The time value in seconds to be converted.
 * @returns {number | null} The time value with precision up to 6 decimal
 * places. Returns null if the input is null.
 *
 * @example
 * getBrowserSafeSeconds(1234.567891012); // returns 1234.567891
 * getBrowserSafeSeconds(null); // returns null
 */
export const getBrowserSafeSeconds = (time: number | null): number | null =>
  time || time === 0 ? parseFloat(time.toFixed(6)) : null;

/**
 * Converts milliseconds to seconds.
 *
 * @param {number} milliseconds - The number of milliseconds to be converted to seconds.
 * @returns {number} The number of seconds equivalent to the given milliseconds.
 * @example
 * convertMillisecondsToSeconds(5000); // returns 5
 * convertMillisecondsToSeconds(250); // returns 0.25
 */
export const convertMillisecondsToSeconds = (milliseconds: number): number =>
  milliseconds / 1000;

/**
 * Converts milliseconds represented as a string to a readable timestamp string in ISO-9075 format.
 *
 * @param {string} millisecondsAsString - The string representation of the number of milliseconds to be converted to a timestamp.
 * @returns {string} The timestamp string in ISO-9075 format representing the given milliseconds.
 * @example
 * convertMillisecondsToReadableTimestamp('1723439120000'); // returns '2023-04-14T14:05:20.000-07:00'
 * convertMillisecondsToReadableTimestamp('1723558212000'); // returns '2023-04-15T01:30:12.000-07:00'
 */
export const convertMillisecondsToReadableTimestamp = (
  millisecondsAsString: string
): string => formatISO9075(new Date(Number(millisecondsAsString)));

/**
 * Formats the given bit depth to a readable value.
 *
 * @param {number} value - The bit rate to format.
 * @returns {Object} An object containing the value, unit, and display value.
 * @example
 * formatBitDepth(16); // returns { value: 16, unit: 'bit', displayValue: '16-bit' }
 */
export function formatBitDepth(value: number): FormattedNumber {
  if (value <= 0) {
    return { value: 0, displayValue: '0-bit', unit: 'bit' };
  }

  return {
    value,
    unit: 'bit',
    displayValue: `${value.toString()}-bit`,
  };
}

/**
 * Formats the given channels to a readable value.
 *
 * @param {number} value - The channel value to format.
 * @returns {Object} An object containing the value, unit, and display value.
 * @example
 * formatChannels(2); // returns { value: 2, unit: 'channel', displayValue: '2 channels' }
 */
export function formatChannels(value: number): FormattedNumber {
  if (value <= 0) {
    return { value: 0, displayValue: '0 channels', unit: 'channel' };
  }

  return {
    value,
    unit: 'channel',
    displayValue: `${value.toString()} channel${value > 1 ? 's' : ''}`,
  };
}

/**
 * Formats a frame rate value to fps with maximum two decimal places.
 *
 * @param fps The frame rate value to format.
 * @returns An object containing the formatted frame rate value, unit ('fps'), and display value as a string.
 * @example
 * const fps = formatToFps(30); // returns { value: 30, displayValue: '30 fps', unit: 'fps' }
 * const fps = formatToFps(29.97); // returns { value: 29.97, displayValue: '29.97 fps', unit: 'fps' }
 */
export function formatFps(fps: number): FormattedNumber {
  if (fps <= 0) {
    return { value: 0, displayValue: '0 fps', unit: 'fps' };
  }

  const unit = 'fps';
  const value = Number.isInteger(fps) ? fps : parseFloat(fps.toFixed(2));
  const displayValue = `${value} ${unit}`;

  return { value, unit, displayValue };
}

/**
 * Formats a value from any length unit to pixels (px).
 *
 * @param pixels to pixel value to format.
 * @returns An object with `value`, `displayValue`, and `unit` properties.
 */
export function formatPixels(pixels: number): FormattedNumber {
  if (pixels <= 0 || Number.isNaN(pixels)) {
    return { value: 0, displayValue: '0 px', unit: 'px' };
  }

  const value = parseFloat(pixels.toFixed(0));
  const displayValue = `${value} px`;
  return {
    value,
    displayValue,
    unit: 'px',
  };
}

/**
 * Formats a bit rate to the specified unit (bps, kbps, Mbps, or Gbps).
 *
 * @param bits The bit rate value to format.
 * @returns An object with the formatted value, display value and unit.
 */
export function formatBitRate(bits: number): FormattedNumber {
  if (bits <= 0 || Number.isNaN(bits)) {
    return { value: 0, displayValue: '0 bps', unit: 'bps' };
  }
  const units: string[] = ['bps', 'kbps', 'Mbps', 'Gbps'];
  const base = 1000;
  const exponent = Math.floor(Math.log(bits) / Math.log(base));
  const rate = bits / base ** exponent;
  const value = Number.isInteger(rate) ? rate : parseFloat(rate.toFixed(2));
  const unit = units[exponent];
  const displayValue = `${value} ${unit}`;

  return { value, displayValue, unit };
}

/**
 * Formats a sample rate in Hz to the appropriate frequency unit (Hz, kHz, MHz, or GHz).
 *
 * @param {number} samplesPerSecond - The sample rate in Hz.
 * @returns {object} - An object with the formatted sample rate, displayValue, and unit.
 * @example
 * const sampleRate = formatSampleRate(96_000) // {value: 96, displayValue: '96 kHz', unit: 'kHz'}
 */
export function formatSampleRate(
  samplesPerSecond: number
): FormattedNumber | null {
  if (
    samplesPerSecond === 0 ||
    isNaN(samplesPerSecond) ||
    typeof samplesPerSecond !== 'number'
  ) {
    return { value: 0, displayValue: '0 Hz', unit: 'Hz' };
  }

  const units: string[] = ['Hz', 'kHz', 'MHz', 'GHz'];
  const base = 1000;
  const exponent = Math.floor(Math.log(samplesPerSecond) / Math.log(base));
  const rate = samplesPerSecond / base ** exponent;
  const value = Number.isInteger(rate) ? rate : parseFloat(rate.toFixed(1));
  const unit = units[exponent];
  const displayValue = `${value} ${unit}`;

  return { value, displayValue, unit };
}

/**
 * Formats dollars into a string with commas
 * @param {Number} value - a dollar value
 * @param {Boolean} decimal - whether to include two decimal points
 * @param {Boolean} removeTrailingZeros - removes trailing zeros

 */
export const formatMoney = (
  value: number,
  decimal: boolean = true,
  removeTrailingZeros: boolean = false
) => {
  /** in the future, `opts` and the `localeString` could be passed in as a 3rd parameter to enable internationalization */
  const precision = decimal ? 2 : 0;
  const opts = {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  } satisfies Intl.NumberFormatOptions;

  const formatter = new Intl.NumberFormat('en-US', opts);

  if (removeTrailingZeros) {
    return formatter.format(round(value, precision)).replace(/\.00$/, '');
  }

  return formatter.format(round(value, precision));
};

/**
 * Formats dollars into a string with commas from cents
 * @param {Number} cents - a value in cents
 */
export const formatCents = (cents: number, removeTrailingZeros?: boolean) =>
  formatMoney(cents / 100, true, removeTrailingZeros);

/**
 * Formats value to title case
 * @param {string} str - value to be converted
 */
export const formatTitleCase = (str: string) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str?.slice(1)?.toLowerCase();
};
